import React, {useEffect, useState} from "react";

import ValidityForm, {ValidityFormValues} from "./ValidityForm";
import MembershipOffer from "./MembershipOffer";
import InformationForm, {InformationFormValues} from "./InformationForm";
import SailingInfosForm, {SailingInfos} from "./SailingInfosForm";
import {APIGetValidityResponse, APIMembership, APIPostRequestMembershipResponse} from "../../services/facade";
import EULAContainer from "./EULAContainer";
import {Button} from "../../designSystem/Button";
import AffiliationForm, {AffiliationValues} from "./AffiliationForm";
import MembershipRecap from "./MembershipRecap";
import Container, {InfoPannelType} from "../Container";
import ValidityformRecap from "./ValidityFormRecap";
import {postParentAuthFile, requestMembership} from "../../services/controllers";
import SimpleIndicator, {SimpleIndicatorStyle} from "../../designSystem/SimpleIndicator";
import CertificateContainer from "./CertificateContainer";
import Lock from "../../designSystem/icons/Lock";

export interface ValidityStateInterface {
  formValues: ValidityFormValues;
  isValid?: boolean;
  reason?: string;
  availableMemberships?: Membership[];
}

export interface Membership {
    code: string,
    name: string,
    price: number,
    year: number,
    validityStart: Date,
    validityEnd: Date,
}

const MembershipForm = () => {
    // Necessary to declare initial values to avoid console error
    // https://formik.org/docs/api/formik#initialvalues-values

    // POUR TEST RAPIDE

    // const [validity, setValidity] = useState<ValidityStateInterface | null>({
    //     formValues: {
    //         firstName: 'H',
    //         lastName: 'H',
    //         gender: Gender.Homme,
    //         birthDate: new Date('2015-12-21'),
    //     },
    //     isValid: true,
    //     availableMemberships: [{
    //         code: '23J',
    //         name: 'Licence Jeune',
    //         price: 30,
    //         year: 2023
    //     }]
    // })
    // const [memberInfos, setMemberInfos] = useState<InformationFormValues>({
    //     birthName: 'H',
    //     birthCity: 'Paris 14',
    //     address: '41 rue Saint Hélier',
    //     addressDetails: '',
    //     zipCode: '35000',
    //     city: 'RENNES',
    //     country: 'FRANCE',
    //     phone: '0669382364',
    //     email: 'hugoherrou@hotmail.fr',
    // })
    // const [sailingInfos, setSailingInfos] = useState<SailingInfos | null>({
    //     discipline: {
    //         SECTEUR_CODE: 'P',
    //         SECTEUR_LIBELLE: 'Windsurf'
    //     },
    //     activity: {
    //         code: 'L',
    //         lib: 'Loisir'
    //     }
    // })

    // FIN TEST

    const [validity, setValidity] = useState<ValidityStateInterface | null>(null)
    const [memberInfos, setMemberInfos] = useState<InformationFormValues>({
        birthName: '',
        birthCity: '',
        address: '',
        addressDetails: '',
        zipCode: '',
        city: '',
        country: '',
        phone: '',
        email: '',
    })
    const [sailingInfos, setSailingInfos] = useState<SailingInfos | null>(null)

    const [affiliationInfos, setAffiliationInfos] = useState<AffiliationValues | null>(null)
    const [parentAuthFile, setParentAuthFile] = useState<File | null>(null)
    const [memberInfosValidated, setMemberInfosValidated] = useState<boolean>(false)
    const [sailingInfosValidated, setSailingInfosValidated] = useState<boolean>(false)
    const [affiliationInfosValidated, setAffiliationInfosValidated] = useState<boolean>(false)
    const [eulaChecked, setEULAChecked] = useState<boolean>(false)
    const [confirmationPage, setConfirmationPage] = useState<boolean>(false)
    const [paymentRedirect, setPaymentRedirect] = useState<boolean>(false)

    const [isMinor, setIsMinor] = useState<boolean>(false)

    useEffect(() => {
        if (validity?.isValid === true) {
            const today = new Date();
            const birthDate = validity.formValues.birthDate
            if (birthDate) {
              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              const dayDiff = today.getDate() - birthDate.getDate();

              if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                age--;
              }
              setIsMinor(age < 18)
            }
        }
    }, [validity?.isValid, validity?.formValues.birthDate])

    return (
      <div className={"w-full flex flex-col gap-y-6"}>
        {(!validity || validity.isValid === undefined) && (
          <ValidityForm
            initialValues={{
              firstName: "",
              lastName: "",
              gender: undefined,
              birthDate: undefined,
            }}
            setValues={(
              formValues: ValidityFormValues,
              response: APIGetValidityResponse
            ) => {
              setValidity({
                formValues: formValues,
                isValid: response.ELIGIBLE,
                reason: response.RAISON,
                availableMemberships:
                  response.LICENCES_DISPOS &&
                  response.LICENCES_DISPOS.map((l: APIMembership) => ({
                    code: l.CODE,
                    name: l.LIBELLE,
                    price: l.PRIX_LICENCE,
                    year: l.ANNEE_LICENCE,
                    validityStart: new Date(l.VALIDITE_DEBUT),
                    validityEnd: new Date(l.VALIDITE_FIN),
                  })),
              });
              setMemberInfos({
                ...memberInfos,
                birthName: formValues.lastName,
              });
            }}
          />
        )}
        {!!validity &&
          validity.isValid &&
          !!validity.availableMemberships &&
          validity.availableMemberships.length > 0 &&
          !confirmationPage && (
            <React.Fragment>
              <MembershipOffer
                className={"w-full"}
                name={validity.availableMemberships[0].name}
                price={validity.availableMemberships[0].price}
                period={[
                    validity.availableMemberships[0].validityStart,
                    validity.availableMemberships[0].validityEnd,
                ]}
                validityFormValues={validity.formValues}
                resetValidityFormValues={() => setValidity(null)}
              />
              <InformationForm
                titleNumber={1}
                initialValues={memberInfos}
                setValues={setMemberInfos}
                setValidated={setMemberInfosValidated}
                isValidated={memberInfosValidated}
              />
              <SailingInfosForm
                titleNumber={2}
                initialValues={{
                  discipline: sailingInfos?.discipline.SECTEUR_CODE || "",
                  level: sailingInfos?.activity.code || "",
                  handi:
                    sailingInfos?.handi !== undefined
                      ? sailingInfos?.handi.toString()
                      : undefined,
                }}
                setValues={setSailingInfos}
                setValidated={setSailingInfosValidated}
                isValidated={sailingInfosValidated}
              />
              <AffiliationForm
                titleNumber={3}
                initialValues={{
                  // @ts-ignore
                  club: affiliationInfos?.club,
                  ligue: affiliationInfos?.ligue?.ST_CODE,
                }}
                setValues={setAffiliationInfos}
                setValidated={setAffiliationInfosValidated}
                validated={affiliationInfosValidated}
              />
              {isMinor && <CertificateContainer titleNumber={4} setFile={setParentAuthFile}/>}
              <EULAContainer
                titleNumber={isMinor ? 5 : 4}
                isValidated={eulaChecked}
                setIsValidated={(b: boolean) => setEULAChecked(b)}
                healthCheck={isMinor}
              />
              <div className={"flex justify-center"}>
                <Button
                  disabled={
                    !memberInfosValidated ||
                    !sailingInfosValidated ||
                    !affiliationInfosValidated ||
                    (isMinor && !parentAuthFile) ||
                    !eulaChecked
                  }
                  onClick={() => setConfirmationPage(true)}
                >
                  {`Confirmer et payer ${validity.availableMemberships[0].price} €`}
                </Button>
              </div>
              <div className={"flex gap-x-2 gap-y-2 items-center justify-center"}>
                <div className={"w-6 h-6"}>
                    <Lock className={"fill-blue"}/>
                </div>
                <div className={"text-grey--400"}>Paiement sécurisé</div>
              </div>
            </React.Fragment>
          )}
        {!!validity &&
          validity.isValid &&
          !!validity.availableMemberships &&
          validity.availableMemberships.length > 0 &&
          confirmationPage && (
            <React.Fragment>
              <div
                className={`flex flex-col gap-y-6 ${
                  paymentRedirect ? "pointer-events-none opacity-80" : ""
                }`}
              >
                <MembershipOffer
                  className={"w-full"}
                  name={validity.availableMemberships[0].name}
                  price={validity.availableMemberships[0].price}
                  period={[
                      validity.availableMemberships[0].validityStart,
                      validity.availableMemberships[0].validityEnd,
                  ]}
                  validityFormValues={validity.formValues}
                  resetValidityFormValues={() => setValidity(null)}
                />
                <MembershipRecap
                  birthName={memberInfos.birthName}
                  birthCity={memberInfos.birthCity}
                  address={memberInfos.address}
                  zipCode={memberInfos.zipCode}
                  city={memberInfos.city}
                  country={memberInfos.country}
                  phone={memberInfos.phone}
                  email={memberInfos.email}
                  discipline={sailingInfos?.discipline.SECTEUR_LIBELLE || ""}
                  activity={sailingInfos?.activity.lib || ""}
                  handi={sailingInfos?.handi}
                  // @ts-ignore
                  affiliation={
                    affiliationInfos?.club?.ST_LIBELLE ||
                    `Ligue\u00A0${affiliationInfos?.ligue?.NOM}`
                  }
                  modify={() => setConfirmationPage(false)}
                />
              </div>
              <Button
                isLoading={paymentRedirect}
                onClick={() => {
                  setPaymentRedirect(true);
                  requestMembership({
                    // @ts-ignore
                    NOM: validity.formValues.lastName,
                    // @ts-ignore
                    PRENOM: validity.formValues.firstName,
                    // @ts-ignore
                    DATE_DE_NAISSANCE:
                      validity.formValues.birthDate?.toDateString(),
                    //@ts-ignore
                    SEXE: validity.formValues.gender === 0 ? "M" : "F",
                    EMAIL: memberInfos.email,
                    MOBILE: memberInfos.phone,
                    ADR1: memberInfos.address,
                    ADR2: memberInfos.addressDetails,
                    CODE_POSTAL: memberInfos.zipCode,
                    VILLE: memberInfos.city,
                    PAYS: memberInfos.country,
                    HANDICAP: sailingInfos?.handi,
                    // @ts-ignore
                    SECTEUR_CODE: sailingInfos?.discipline.SECTEUR_CODE,
                    // @ts-ignore
                    ACTIVITE_CODE: sailingInfos?.activity.code,
                    NAISSANCE_NOM: memberInfos.birthName,
                    NAISSANCE_COMMUNE: memberInfos.birthCity,
                    // @ts-ignore
                    ST_CODE_SOUHAITE:
                      affiliationInfos?.club?.ST_CODE ||
                      affiliationInfos?.ligue?.ST_CODE,
                    // @ts-ignore
                    TL_CODE: validity.availableMemberships[0].code,
                    CGU: eulaChecked,
                    QUEST_SANTE: isMinor && eulaChecked,
                  }).then((response: APIPostRequestMembershipResponse) => {
                    const hash = response.HASH_NPSN_STR;
                    if (hash) {
                        if (!!parentAuthFile) {
                            postParentAuthFile({file: parentAuthFile, draftHash: hash}).then(() => {
                                window.location.replace(
                                    `https://www.ffvoile.fr/ffv/includes/LicenceIndiv_Boutique.aspx?hash=${hash}`
                                )
                            })
                        } else {
                          window.location.replace(
                            `https://www.ffvoile.fr/ffv/includes/LicenceIndiv_Boutique.aspx?hash=${hash}`
                          );
                        }
                    }
                  });
                }}
              >
                {!paymentRedirect
                  ? `Procéder au paiement de ${validity.availableMemberships[0].price} €`
                  : "Redirection vers la boutique..."}
              </Button>
              <div className={"flex gap-x-2 gap-y-2 items-center justify-center"}>
                  <div className={"w-6 h-6"}>
                    <Lock className={"fill-blue"}/>
                  </div>
                  <div className={"text-grey--400"}>Vous allez être redirigé vers notre plateforme de paiement sécurisée</div>
              </div>
            </React.Fragment>
          )}
        {!!validity && validity.isValid === false && (
          <Container
            infoPanel={{
              message: `Vous n'êtes pas éligible à la souscription en ligne`,
              type: InfoPannelType.ERROR,
            }}
          >
            <div className={"flex flex-col gap-y-2"}>
              <ValidityformRecap
                validityFormValues={validity.formValues}
                modify={() => setValidity(null)}
              />
              <SimpleIndicator
                title={"Motif de non éligibilité"}
                value={validity.reason || "Non défini"}
                style={SimpleIndicatorStyle.INLINE}
              />
            </div>
          </Container>
        )}
      </div>
    );
}

export default MembershipForm;
